/**
 * App entry: TanStack Router (code-based routes) + Convex provider.
 *
 * Note: v1 ships as a client-side SPA. Convex owns all data and realtime
 * updates, so server rendering adds no value here; the router is the same
 * `@tanstack/react-router` that TanStack Start wraps, so layering Start's
 * SSR shell on top later is mechanical (see docs/decisions.md).
 */

import type { GameMode } from "@domino/types";
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  useSearch,
} from "@tanstack/react-router";
import { ConvexProvider } from "convex/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { convex } from "./lib/convex";
import HomePage from "./routes/home";
import LocalPage from "./routes/local";
import RoomPage from "./routes/room";

// Bundled typefaces: Cairo (UI), Rakkas (display), Amiri (elegant naskh).
import "@fontsource/cairo/arabic-400.css";
import "@fontsource/cairo/arabic-600.css";
import "@fontsource/cairo/arabic-700.css";
import "@fontsource/cairo/arabic-800.css";
import "@fontsource/cairo/latin-400.css";
import "@fontsource/cairo/latin-600.css";
import "@fontsource/cairo/latin-700.css";
import "@fontsource/cairo/latin-800.css";
import "@fontsource/rakkas/arabic-400.css";
import "@fontsource/rakkas/latin-400.css";
import "@fontsource/amiri/arabic-400.css";
import "@fontsource/amiri/arabic-700.css";
import "./styles.css";

const rootRoute = createRootRoute({
  component: () => (
    <div className="app">
      <Outlet />
    </div>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePage,
});

const localRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/local",
  validateSearch: (search: Record<string, unknown>): { mode: GameMode; target?: number } => {
    const mode = search.mode;
    const target = Number(search.target);
    return {
      mode: mode === "1v1" || mode === "3p" || mode === "2v2" ? mode : "2v2",
      target: Number.isFinite(target) && target > 0 ? target : 100,
    };
  },
  component: function LocalRoute() {
    const { mode, target } = useSearch({ from: "/local" });
    return <LocalPage mode={mode} target={target} />;
  },
});

const roomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/room/$code",
  component: RoomPage,
});

const routeTree = rootRoute.addChildren([indexRoute, localRoute, roomRoute]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function App() {
  if (!convex) return <RouterProvider router={router} />;
  return (
    <ConvexProvider client={convex}>
      <RouterProvider router={router} />
    </ConvexProvider>
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element");
createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
